import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from '../../hooks/use-toast';
import PageMeta from '../PageMeta';
import { track } from '../../services/analytics';

const Login = () => {
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('phone'); // 'phone' or 'otp'
  const [loading, setLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0);   // seconds until resend allowed

  const { sendOTP, login } = useAuth();
  const navigate = useNavigate();

  // Countdown ticker for the resend button
  React.useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const startResendTimer = () => setResendIn(30);   // industry-standard 30s

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (phone.length !== 10) {
      toast({ title: 'Invalid Phone Number', description: 'Please enter a valid 10-digit phone number', variant: 'destructive' });
      return;
    }
    setLoading(true);
    track('cta_otp_send_clicked', { phone_last4: phone.slice(-4) });
    const result = await sendOTP(phone);
    if (result.success) {
      setStep('otp');
      startResendTimer();
      track('otp_sent', { success: true });
      toast({
        title: 'OTP sent on WhatsApp',
        description: `A 6-digit code has been sent to +91 ${phone} on WhatsApp. It expires in 5 minutes.`,
        duration: 6000,
      });
    } else {
      toast({ title: 'Error', description: result.error, variant: 'destructive' });
    }
    setLoading(false);
  };

  const handleResendOTP = async () => {
    if (resendIn > 0 || loading) return;
    setLoading(true);
    track('cta_otp_resend_clicked');
    const result = await sendOTP(phone);
    if (result.success) {
      startResendTimer();
      setOtp('');
      toast({ title: 'OTP resent', description: `New code sent to +91 ${phone} on WhatsApp.` });
    } else {
      toast({ title: 'Error', description: result.error, variant: 'destructive' });
    }
    setLoading(false);
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast({ title: 'Invalid OTP', description: 'Please enter a 6-digit OTP', variant: 'destructive' });
      return;
    }
    setLoading(true);
    track('cta_otp_verify_clicked');
    const result = await login(phone, otp);
    if (result.success) {
      track('login_succeeded', { method: 'otp' });
      toast({ title: 'Login Successful!', description: 'Welcome to RentKollect Manager' });
      navigate('/manager/dashboard');
    } else {
      track('login_failed', { reason: result.error || 'invalid_otp' });
      toast({ title: 'Login Failed', description: result.error || 'Invalid OTP', variant: 'destructive', duration: 7000 });
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-blue-50 flex items-center justify-center p-4">
      <PageMeta
        title="Owner Login — Access your dashboard"
        description="Log in to your RentKollect owner dashboard to manage properties, rooms, tenants and rent collection. WhatsApp OTP sign-in."
        noindex
      />
      <div className="max-w-6xl w-full grid md:grid-cols-2 gap-8 items-center">
        {/* LEFT SIDE — Auth Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
          <div className="text-center mb-6">
            <div className="bg-blue-600 rounded-2xl px-6 py-3 inline-block mb-3">
              <h1 className="text-white font-bold text-2xl">RentKollect</h1>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">For Owners, by an Owner 🏠</h2>
            <p className="text-gray-500 text-sm mt-1">Sign in with WhatsApp OTP — no signup, no waiting.</p>
          </div>

          {step === 'phone' && (
            <form onSubmit={handleSendOTP} className="space-y-5" data-testid="login-phone-form">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                <div className="flex gap-2">
                  <div className="flex items-center gap-2 px-4 py-3 bg-gray-100 rounded-lg">
                    <img src="https://flagcdn.com/in.svg" alt="IN" className="w-6 h-4" />
                    <span className="text-gray-700 font-medium">+91</span>
                  </div>
                  <Input
                    data-testid="login-phone-input"
                    type="tel"
                    placeholder="10-digit WhatsApp number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    className="flex-1 text-lg"
                    required
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading || phone.length !== 10}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg rounded-xl"
                data-testid="login-send-otp-btn"
              >
                {loading ? 'Sending…' : 'Send OTP'}
              </Button>
              <p className="text-[11px] text-center text-gray-500">
                By continuing, you agree to receive a one-time WhatsApp message with your login code.
              </p>
            </form>
          )}

          {step === 'otp' && (
            <form onSubmit={handleVerifyOTP} className="space-y-5" data-testid="login-otp-form">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter OTP sent to +91 {phone}
                </label>
                <Input
                  data-testid="login-otp-input"
                  type="text"
                  placeholder="Enter 6-digit OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-lg text-center tracking-widest"
                  maxLength={6}
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={loading || otp.length !== 6}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg rounded-xl"
                data-testid="login-verify-btn"
              >
                {loading ? 'Verifying…' : 'Verify & Login'}
              </Button>
              <Button
                type="button"
                onClick={() => { setStep('phone'); setOtp(''); }}
                variant="outline"
                className="w-full"
                data-testid="login-change-phone-btn"
              >
                Change Phone Number
              </Button>
            </form>
          )}
        </div>

        {/* RIGHT SIDE — brand card */}
        <div className="hidden md:block">
          <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-3xl p-8 text-white shadow-2xl">
            <h3 className="text-2xl font-bold mb-6">Manage Everything in One Place</h3>
            <div className="space-y-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4"><p className="font-semibold">📊 Real-time Dashboard</p><p className="text-sm text-blue-100">Track occupancy, collections & more</p></div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4"><p className="font-semibold">💰 Automatic Rent Collection</p><p className="text-sm text-blue-100">WhatsApp reminders & UPI payments</p></div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4"><p className="font-semibold">👥 Tenant Management</p><p className="text-sm text-blue-100">Digital KYC & document verification</p></div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4"><p className="font-semibold">📈 Analytics & Reports</p><p className="text-sm text-blue-100">Export to Excel, PDF & more</p></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
